import { css } from "@emotion/react";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { LoadingSpinner } from "../../bits/loading";
import { useTitle } from "../../hooks/useTitle";
import { trpc } from "../../../utils/trpc";
import { ArticleCard } from "./ArticleCard";
import { breadcrumbItem, breadcrumbLink, frjBanner } from "../../bits/frjUtils";

export const FrjTagArticlesPage = () => {
  const { tagSlug } = useParams<{ tagSlug: string }>();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = trpc.getFrjPostsByTag.useQuery(
    { tagSlug: tagSlug ?? "", page },
    { enabled: !!tagSlug },
  );

  const posts = data?.posts;
  const pagination = data?.pagination;

  const tagName =
    posts?.[0]?.tags?.find((t) => t.slug === tagSlug)?.name ?? tagSlug;

  useTitle(`${tagName ?? "Tag"} | FRJ | Compass`);

  if (error) {
    return (
      <div
        css={css`
          padding: 24px;
          font-family: Spectral;
          font-size: 16px;
          color: rgba(18, 23, 42, 0.6);
        `}
      >
        Failed to load articles for this tag.
      </div>
    );
  }

  return (
    <>
      <div css={frjBanner}>Annotate articles and cases in the FRJ</div>

      <div
        css={css`
          display: flex;
          flex-direction: column;
          padding: 24px;
          gap: 24px;
        `}
      >
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 12px;
              `}
            >
              <Link to="/frj" css={breadcrumbLink}>
                FRJ Articles
              </Link>
              <span
                css={css`
                  color: rgba(18, 23, 42, 0.3);
                  font-size: 13px;
                `}
              >
                /
              </span>
              <Link to="/frj/tags" css={breadcrumbLink}>
                Tags
              </Link>
              <span
                css={css`
                  color: rgba(18, 23, 42, 0.3);
                  font-size: 13px;
                `}
              >
                /
              </span>
              <span css={breadcrumbItem}>{tagName}</span>
            </div>
            <h1
              css={css`
                font-family: Spectral;
                font-size: 28px;
                font-weight: bold;
                color: #12172a;
                margin: 0;
              `}
            >
              {tagName}
            </h1>

            <Link
              css={css`
                font-family: Spectral;
                font-size: 16px;
                color: rgba(18, 23, 42, 0.6);
                text-decoration: none;
                &:hover {
                  text-decoration: underline;
                }
              `}
              to="/frj/tags"
            >
              Refine your search by other tags
            </Link>

            {posts && posts.length === 0 && !isLoading && (
              <p
                css={css`
                  font-family: Spectral;
                  font-size: 16px;
                  color: rgba(18, 23, 42, 0.6);
                `}
              >
                No articles found for this tag.
              </p>
            )}

            {posts && posts.length > 0 && (
              <div>
                {posts.map((post) => (
                  <ArticleCard key={post.slug} post={post} tagSlug={tagSlug} />
                ))}
              </div>
            )}

            {pagination && pagination.pages > 1 && (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 16px;
                  padding-top: 8px;
                `}
              >
                <button
                  onClick={() => setPage((p) => p - 1)}
                  disabled={!pagination.prev}
                  css={css`
                    font-family: Urbanist;
                    font-size: 14px;
                    font-weight: 600;
                    padding: 8px 16px;
                    border: 1px solid rgba(0, 31, 43, 0.2);
                    border-radius: 6px;
                    background: white;
                    cursor: pointer;
                    color: #12172a;
                    &:disabled {
                      opacity: 0.35;
                      cursor: default;
                    }
                    &:not(:disabled):hover {
                      border-color: rgba(0, 31, 43, 0.4);
                      background: rgba(0, 31, 43, 0.03);
                    }
                  `}
                >
                  ← Previous
                </button>
                <span
                  css={css`
                    font-family: Urbanist;
                    font-size: 13px;
                    color: rgba(18, 23, 42, 0.5);
                  `}
                >
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!pagination.next}
                  css={css`
                    font-family: Urbanist;
                    font-size: 14px;
                    font-weight: 600;
                    padding: 8px 16px;
                    border: 1px solid rgba(0, 31, 43, 0.2);
                    border-radius: 6px;
                    background: white;
                    cursor: pointer;
                    color: #12172a;
                    &:disabled {
                      opacity: 0.35;
                      cursor: default;
                    }
                    &:not(:disabled):hover {
                      border-color: rgba(0, 31, 43, 0.4);
                      background: rgba(0, 31, 43, 0.03);
                    }
                  `}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
